/**
 * Format Utilities
 * Shared utility functions for date and number parsing/formatting
 */

/**
 * Parse a comma-formatted volume string to a number
 * @param {string} value - Comma-formatted number like "4,895"
 * @returns {number} Parsed integer like 4895
 * @throws {Error} If value cannot be parsed
 *
 * @example
 * parseVolumeString("4,895") // returns 4895
 * parseVolumeString("100,000") // returns 100000
 */
export function parseVolumeString(value) {
    if (!value || typeof value !== 'string') {
        throw new Error(`Invalid volume string: ${value}`);
    }

    const cleaned = value.replace(/,/g, '');
    const parsed = parseInt(cleaned, 10);

    if (isNaN(parsed)) {
        throw new Error(`Cannot parse volume: ${value}`);
    }

    return parsed;
}

/**
 * Convert quote date format to print API format
 * @param {string} dateString - Date in "24 Nov 2025" format
 * @returns {string} Date in "24-Nov-2025" format for print API
 *
 * @example
 * formatDateForPrintApi("24 Nov 2025") // returns "24-Nov-2025"
 */
export function formatDateForPrintApi(dateString) {
    if (!dateString || typeof dateString !== 'string') {
        return '';
    }

    const parts = dateString.trim().split(' ');

    // Expect format: "DD Mon YYYY" (3 parts)
    if (parts.length !== 3) {
        console.warn(`[FormatUtils] Unexpected date format: ${dateString}`);
        return '';
    }

    const [day, month, year] = parts;
    return `${day}-${month}-${year}`;
}

/**
 * Format a number as currency string
 * @param {number} amount - Numeric amount
 * @returns {string} Formatted currency string (e.g., "$512.55")
 *
 * @example
 * formatCurrency(512.55) // returns "$512.55"
 * formatCurrency(1000) // returns "$1,000.00"
 */
export function formatCurrency(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) {
        return '$0.00';
    }

    return `$${amount.toLocaleString('en-NZ', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/**
 * Format a number with comma separators
 * @param {number} value - Numeric value
 * @returns {string} Formatted number string (e.g., "4,895")
 *
 * @example
 * formatNumber(4895) // returns "4,895"
 * formatNumber(100000) // returns "100,000"
 */
export function formatNumber(value) {
    if (typeof value !== 'number' || isNaN(value)) {
        return '0';
    }

    return value.toLocaleString('en-NZ');
}
